import { LookStateDto, SetupDto, SetupSectionDto } from "@growth/dto";
import { Badge, Button, Field, Input, Textarea } from "@growth/ui";
import { Check, CircleCheck, LoaderCircle, Palette, RotateCcw, Search, X } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { RunFeed } from "../components/agent-activity";
import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { formatRelative } from "../lib/format";
import { type PillarDraft, pillarExample, pillarProblem } from "../lib/pillars";
import {
  useAcceptPillars,
  useApplyLook,
  useApproveClaim,
  useConfirmSection,
  useDiscardDraft,
  useRedoSection,
  useRejectClaim,
  useSetup,
  useStartLook,
  useStartSetup,
} from "../lib/research";
import { useClaims } from "../lib/workspace";

/**
 * Brand setup: the researcher reads the website and drafts the profile, claims and pillars. A
 * person confirms each part; agents use none of it until then.
 */
export const SetupPage = () => {
  const { brand } = useActive();
  const setup = useSetup();

  return (
    <section className="max-w-6xl space-y-6">
      <PageHeader eyebrow={brand?.name ?? ""} title="Brand setup">
        Give the researcher the website. It reads the site, looks at competitors and the market, and
        drafts the brand profile and claims. You check each part; agents use nothing until you
        confirm it.
      </PageHeader>
      {setup.isError && <ErrorText>{apiErrorMessage(setup.error)}</ErrorText>}
      {setup.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      {setup.data && <SetupBody setup={setup.data} />}
    </section>
  );
};

const SetupBody = ({ setup }: { setup: SetupDto }) => {
  const confirmed = setup.sections.filter((section) => section.state === "confirmed").length;
  const steps = [
    { label: "Website", done: setup.status === "ready" || setup.status === "running" },
    { label: "Brand profile", done: confirmed === setup.sections.length },
    { label: "Claims", done: setup.status === "ready" && setup.suggestedClaims === 0 },
    { label: "Pillars", done: setup.pillarsDone },
    { label: "Look", done: Boolean(setup.look?.appliedAt) },
  ];
  return (
    <div className="space-y-6">
      <ol aria-label="Setup steps" className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {steps.map((step, index) => (
          <li
            key={step.label}
            className={`border-t-[3px] pt-2 text-sm ${step.done ? "border-accent text-ink" : "border-line text-muted"}`}
          >
            {index + 1} · {step.label}
            {step.done && <Check className="ml-1 inline size-3.5 text-accent" aria-label="done" />}
          </li>
        ))}
      </ol>

      {setup.done && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm">
          <span className="flex items-center gap-2">
            <CircleCheck className="size-5 text-emerald-700" /> Setup is done. The weekly research
            brief is on.
          </span>
          <Link to="/research" className="font-medium text-accent underline">
            Go to Research
          </Link>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-4">
          <Website setup={setup} />
          <LookPanel setup={setup} />
          {setup.status === "running" && (
            <div className="space-y-2">
              <RunFeed runId={setup.run?.id} limit={8} />
              <p className="px-1 text-xs text-muted">
                This takes a few minutes. You can leave this page: the agents keep going, and the
                corner of every page shows what they’re doing.
              </p>
            </div>
          )}
          {setup.status === "failed" && setup.error && (
            <ErrorText>The research stopped: {setup.error}</ErrorText>
          )}
          {setup.status !== "none" && setup.status !== "running" && (
            <>
              {setup.category && (
                <p className="text-sm text-muted">
                  Category: <strong className="text-ink">{setup.category}</strong>
                </p>
              )}
              {setup.sections.map((section) => (
                <SectionCard key={section.kind} section={section} />
              ))}
              <SuggestedClaims />
              <Pillars setup={setup} />
            </>
          )}
        </div>
        <aside className="space-y-4">
          <RunLog setup={setup} />
          <p className="text-xs leading-relaxed text-muted">
            Everything here can be changed later in Brand profile and Claims. The researcher's
            instructions are on the Agents page.
          </p>
        </aside>
      </div>
    </div>
  );
};

const ROLE_LABELS: Record<string, string> = {
  background: "Background",
  surface: "Surface",
  text: "Text",
  secondaryText: "Quiet text",
  accent: "Accent",
  highlight: "Highlight",
};

/**
 * What the brand designer found on the website: palette, type, logo and icons. A draft like the
 * profile: "Use this look" makes it the brand kit that slides and posts are drawn with.
 */
const LookPanel = ({ setup }: { setup: SetupDto }) => {
  const { atLeast } = useActive();
  const apply = useApplyLook();
  const again = useStartLook();
  const look = setup.look;
  if (!look) {
    if (!setup.websiteUrl || !atLeast("OWNER")) return null;
    return (
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-white px-5 py-3 text-sm">
        <span className="flex items-center gap-2 text-muted">
          <Palette className="size-4" /> The brand designer can read your colours, type and logo
          from the site.
        </span>
        <Button variant="secondary" onClick={() => again.mutate()} disabled={again.isPending}>
          Read the look
        </Button>
      </div>
    );
  }
  return (
    <section className="space-y-4 rounded-xl border border-line bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-serif text-lg text-ink">
          <Palette className="size-4 text-muted" /> Brand look
        </h2>
        {look.appliedAt ? (
          <Badge>In use</Badge>
        ) : look.status === "ready" ? (
          <Badge>Check this</Badge>
        ) : null}
      </div>
      {look.status === "running" && <RunFeed runId={look.runId} limit={5} />}
      {look.status === "failed" && <ErrorText>{look.error}</ErrorText>}
      {look.status !== "failed" && <LookDetails look={look} />}
      <div className="flex flex-wrap items-center gap-3 border-t border-line pt-4 text-sm">
        {look.status === "ready" && !look.appliedAt && atLeast("EDITOR") && (
          <Button onClick={() => apply.mutate()} disabled={apply.isPending}>
            <Check className="size-4" /> Use this look
          </Button>
        )}
        {look.status !== "running" && atLeast("OWNER") && (
          <Button variant="secondary" onClick={() => again.mutate()} disabled={again.isPending}>
            <RotateCcw className="size-4" /> Read again
          </Button>
        )}
        <span className="text-muted">
          {look.appliedAt
            ? `In use since ${formatRelative(look.appliedAt)}: slides and posts are drawn with it.`
            : "Nothing changes until you use it."}
        </span>
      </div>
      {(apply.isError || again.isError) && (
        <ErrorText>{apiErrorMessage(apply.error ?? again.error)}</ErrorText>
      )}
    </section>
  );
};

const LookDetails = ({ look }: { look: LookStateDto }) => {
  const roleOf = (hex: string) =>
    Object.entries(look.roles).find(([, value]) => value === hex)?.[0];
  return (
    <div className="grid gap-5 md:grid-cols-[14rem_minmax(0,1fr)]">
      <div className="space-y-3">
        {look.screenshotKey && (
          <img
            src={`/api/files/${look.screenshotKey}`}
            alt={`The home page of ${look.siteTitle || "the site"}`}
            className="aspect-[16/10] w-full rounded-lg border border-line object-cover object-top"
          />
        )}
        {look.logoKey && (
          <div
            className="inline-flex rounded-lg border border-line px-3 py-2"
            style={{ background: look.logoOn ?? "transparent" }}
          >
            <img src={`/api/files/${look.logoKey}`} alt="Logo" className="max-h-8 max-w-48" />
          </div>
        )}
      </div>
      <div className="min-w-0 space-y-4 text-sm">
        {look.summary && <p className="font-serif text-base italic text-ink">“{look.summary}”</p>}
        {look.colors.length > 0 && (
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3" aria-label="Colours">
            {look.colors.map((color) => (
              <li key={color.hex} className="flex min-w-0 items-center gap-2">
                <span
                  className="size-8 flex-none rounded-full ring-1 ring-inset ring-black/10"
                  style={{ background: color.hex }}
                />
                <span className="min-w-0">
                  <span className="block truncate text-ink">
                    {color.name || ROLE_LABELS[roleOf(color.hex) ?? ""] || color.use}
                  </span>
                  <span className="block font-mono text-xs text-muted">
                    {color.hex}
                    {roleOf(color.hex) ? ` · ${ROLE_LABELS[roleOf(color.hex)!]}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[6rem_minmax(0,1fr)]">
          {look.fonts.display && (
            <>
              <dt className="text-muted">Headlines</dt>
              <dd className="text-ink">{look.fonts.display}</dd>
            </>
          )}
          {look.fonts.body && (
            <>
              <dt className="text-muted">Text</dt>
              <dd className="text-ink">{look.fonts.body}</dd>
            </>
          )}
          {(look.icons.library || look.icons.style) && (
            <>
              <dt className="text-muted">Icons</dt>
              <dd className="flex flex-wrap items-center gap-2 text-ink">
                {look.icons.samples.slice(0, 8).map((src, index) => (
                  <img key={index} src={src} alt="" className="size-5 object-contain" />
                ))}
                {[look.icons.library, look.icons.style].filter(Boolean).join(" · ")}
              </dd>
            </>
          )}
          {look.imagery && (
            <>
              <dt className="text-muted">Imagery</dt>
              <dd className="text-ink">{look.imagery}</dd>
            </>
          )}
          {look.shapes && (
            <>
              <dt className="text-muted">Shapes</dt>
              <dd className="text-ink">{look.shapes}</dd>
            </>
          )}
          {look.social.length > 0 && (
            <>
              <dt className="text-muted">Found on</dt>
              <dd className="flex flex-wrap gap-x-3 text-ink">
                {look.social.map((profile) => (
                  <a
                    key={profile.platform}
                    href={profile.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-accent underline"
                  >
                    {profile.platform === "x" ? "X" : profile.platform}
                  </a>
                ))}
              </dd>
            </>
          )}
        </dl>
        {look.note && <p className="text-xs text-muted">{look.note}</p>}
      </div>
    </div>
  );
};

const Website = ({ setup }: { setup: SetupDto }) => {
  const { atLeast } = useActive();
  const start = useStartSetup();
  const [url, setUrl] = useState(setup.websiteUrl ?? "");
  const [steer, setSteer] = useState("");
  const [open, setOpen] = useState(setup.status === "none");
  if (!atLeast("OWNER")) {
    return setup.status === "none" ? (
      <p className="rounded-xl border border-line bg-white p-5 text-sm text-muted">
        An owner of this workspace starts brand setup.
      </p>
    ) : null;
  }
  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-line bg-white px-5 py-3 text-sm">
        <span>
          Website: <strong>{setup.websiteUrl}</strong>
        </span>
        {setup.status !== "running" && (
          <Button variant="ghost" onClick={() => setOpen(true)}>
            <RotateCcw className="size-4" /> Research again
          </Button>
        )}
      </div>
    );
  }
  const submit = (event: FormEvent) => {
    event.preventDefault();
    start.mutate(
      { websiteUrl: url.trim(), ...(steer.trim() ? { steer: steer.trim() } : {}) },
      { onSuccess: () => setOpen(false) },
    );
  };
  return (
    <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-5">
      <h2 className="font-serif text-xl text-ink">Start from the website</h2>
      <Field label="Website">
        {(props) => (
          <Input
            {...props}
            type="url"
            required
            placeholder="https://www.example.com"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
        )}
      </Field>
      <Field
        label="Anything the researcher should know (optional)"
        hint="e.g. We only sell in Australia. Our buyers are also university careers teams."
      >
        {(props) => (
          <Textarea
            {...props}
            rows={2}
            maxLength={2000}
            value={steer}
            onChange={(event) => setSteer(event.target.value)}
          />
        )}
      </Field>
      {setup.status !== "none" && (
        <p className="text-xs text-amber-700">
          Researching again replaces the drafts waiting for review. Confirmed parts stay as they
          are.
        </p>
      )}
      {start.isError && <ErrorText>{apiErrorMessage(start.error)}</ErrorText>}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={start.isPending}>
          {start.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
          Research this brand
        </Button>
        {setup.status !== "none" && (
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        )}
      </div>
      <p className="text-xs text-muted">
        Counts toward this brand's weekly research budget (US$2 unless you change it).
      </p>
    </form>
  );
};

const STATE: Record<SetupSectionDto["state"], { label: string; className: string }> = {
  confirmed: { label: "Confirmed", className: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  check: { label: "Check this", className: "bg-amber-50 text-amber-800 ring-amber-200" },
  empty: { label: "Nothing yet", className: "" },
  redoing: { label: "Redoing…", className: "bg-blue-50 text-accent ring-blue-200" },
};

const SectionCard = ({ section }: { section: SetupSectionDto }) => {
  const { atLeast } = useActive();
  const canEdit = atLeast("EDITOR");
  const confirm = useConfirmSection();
  const discard = useDiscardDraft();
  const redo = useRedoSection();
  const [mode, setMode] = useState<"view" | "edit" | "redo">("view");
  const [text, setText] = useState(section.draftBody ?? section.body);
  const [note, setNote] = useState("");
  const shown = section.draftBody ?? section.body;
  const error = confirm.error ?? discard.error ?? redo.error;

  return (
    <article
      className={`space-y-3 rounded-xl border bg-white p-5 ${section.state === "check" ? "border-amber-300" : "border-line"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-serif text-xl text-ink">{section.label}</h2>
          <p className="text-xs text-muted">{section.about}</p>
        </div>
        <Badge className={STATE[section.state].className}>
          {section.state === "redoing" && <LoaderCircle className="size-3 animate-spin" />}
          {STATE[section.state].label}
        </Badge>
      </div>

      {section.state === "check" && section.body.trim() && (
        <p className="text-xs text-muted">
          A new draft is waiting. The version in use stays until you confirm.
        </p>
      )}

      {mode === "edit" ? (
        <Textarea
          aria-label={section.label}
          rows={10}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      ) : shown ? (
        <div className="whitespace-pre-line break-words text-sm leading-relaxed text-ink">
          {shown}
        </div>
      ) : (
        <p className="text-sm text-muted">Nothing here yet.</p>
      )}

      {section.state === "check" && (
        <div className="space-y-1">
          {section.draftNote && (
            <p className="text-xs text-amber-800">Researcher's note: {section.draftNote}</p>
          )}
          {section.draftSources.length > 0 && (
            <p className="text-xs text-muted">
              Sources:{" "}
              {section.draftSources.map((source, index) => (
                <span key={source.url}>
                  {index > 0 && ", "}
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-accent underline"
                  >
                    {source.title || new URL(source.url).hostname}
                  </a>
                </span>
              ))}
            </p>
          )}
        </div>
      )}

      {mode === "redo" && (
        <div className="flex flex-wrap gap-2">
          <Input
            aria-label="What should change"
            placeholder="e.g. We also sell in New Zealand"
            className="min-w-64 flex-1"
            maxLength={1000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <Button
            disabled={note.trim().length < 3 || redo.isPending}
            onClick={() =>
              redo.mutate({ kind: section.kind, note }, { onSuccess: () => setMode("view") })
            }
          >
            Redo
          </Button>
          <Button variant="ghost" onClick={() => setMode("view")}>
            Cancel
          </Button>
        </div>
      )}

      {canEdit && section.state !== "redoing" && mode !== "redo" && (
        <div className="flex flex-wrap gap-2">
          {mode === "edit" ? (
            <>
              <Button
                disabled={confirm.isPending || !text.trim()}
                onClick={() =>
                  confirm.mutate(
                    { kind: section.kind, body: text, baseVersion: section.version },
                    { onSuccess: () => setMode("view") },
                  )
                }
              >
                <Check className="size-4" /> Confirm my version
              </Button>
              <Button variant="ghost" onClick={() => setMode("view")}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              {section.state === "check" && (
                <Button
                  disabled={confirm.isPending}
                  onClick={() =>
                    confirm.mutate({
                      kind: section.kind,
                      body: section.draftBody ?? "",
                      baseVersion: section.version,
                    })
                  }
                >
                  <Check className="size-4" /> Looks right
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  setText(shown);
                  setMode("edit");
                }}
              >
                Edit
              </Button>
              <Button variant="secondary" onClick={() => setMode("redo")}>
                <RotateCcw className="size-4" /> Redo with a note
              </Button>
              {section.state === "check" && section.body.trim() && (
                <Button
                  variant="ghost"
                  disabled={discard.isPending}
                  onClick={() => discard.mutate(section.kind)}
                >
                  <X className="size-4" /> Keep the current version
                </Button>
              )}
            </>
          )}
        </div>
      )}
      {error && <ErrorText>{apiErrorMessage(error)}</ErrorText>}
    </article>
  );
};

export const SuggestedClaims = () => {
  const { atLeast } = useActive();
  const claims = useClaims();
  const approve = useApproveClaim();
  const reject = useRejectClaim();
  const suggested = claims.data?.filter((claim) => claim.status === "SUGGESTED") ?? [];
  return (
    <article className="space-y-3 rounded-xl border border-line bg-white p-5">
      <div>
        <h2 className="font-serif text-xl text-ink">Claims found on the site</h2>
        <p className="text-xs text-muted">
          Facts the brand states about itself on its own pages. Agents may only state approved
          claims.
        </p>
      </div>
      {suggested.length === 0 && (
        <p className="text-sm text-muted">Nothing waiting. Manage all claims on the Claims page.</p>
      )}
      <ul className="divide-y divide-line">
        {suggested.map((claim) => (
          <li key={claim.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm text-ink">{claim.text}</p>
              {claim.note && <p className="text-xs text-muted">{claim.note}</p>}
              {claim.source && (
                <a
                  href={claim.source}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-accent underline"
                >
                  {claim.source}
                </a>
              )}
            </div>
            {atLeast("EDITOR") && (
              <div className="flex gap-2">
                <Button
                  disabled={approve.isPending}
                  onClick={() => approve.mutate({ id: claim.id })}
                >
                  Approve
                </Button>
                <Button
                  variant="secondary"
                  disabled={reject.isPending}
                  onClick={() => reject.mutate({ id: claim.id, reason: "" })}
                >
                  Not true
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {(approve.error ?? reject.error) && (
        <ErrorText>{apiErrorMessage(approve.error ?? reject.error)}</ErrorText>
      )}
    </article>
  );
};

const PILLAR_COLUMNS = "sm:grid-cols-[auto_minmax(0,17rem)_minmax(0,1fr)]";

/**
 * The researcher's pillar suggestions. Each needs a sentence saying what every post in it does
 * (the writer follows it). Anyone unsure gets an example built from the pillar's name, and one
 * click puts it in the box to edit.
 */
export const Pillars = ({ setup }: { setup: SetupDto }) => {
  const { atLeast } = useActive();
  const accept = useAcceptPillars();
  const [chosen, setChosen] = useState(() =>
    setup.pillars.map((pillar) => ({ ...pillar, on: true })),
  );
  const [tried, setTried] = useState(false);
  if (setup.status !== "ready") return null;
  const problems = chosen.map(pillarProblem);
  const blocked = problems.some(Boolean);
  const needSentence = chosen
    .map((pillar, index) => ({ pillar, index }))
    .filter(({ pillar, index }) => problems[index] === "Needs a sentence." && pillar.on);

  const update = (index: number, change: Partial<PillarDraft>) =>
    setChosen(chosen.map((item, i) => (i === index ? { ...item, ...change } : item)));
  const useExamples = () =>
    setChosen(
      chosen.map((item, i) =>
        problems[i] === "Needs a sentence."
          ? { ...item, format: pillarExample(item.name, i) }
          : item,
      ),
    );

  return (
    <article className="space-y-4 rounded-xl border border-line bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-serif text-xl text-ink">Content pillars</h2>
          <p className="text-sm text-muted">
            Recurring themes the agents write within. For each one, write a sentence saying what
            every post in it does: the writer follows it.{" "}
            {setup.existingPillars > 0 && `This brand has ${setup.existingPillars} already.`}
          </p>
        </div>
        {setup.pillarsDone && <Badge tone="success">Decided</Badge>}
      </div>
      {setup.pillarsDone ? (
        <p className="text-sm text-muted">
          Change pillars any time in{" "}
          <Link to="/content-setup" className="text-accent underline">
            Content setup
          </Link>
          .
        </p>
      ) : (
        <>
          <div
            aria-hidden="true"
            className={`hidden gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted sm:grid ${PILLAR_COLUMNS}`}
          >
            <span className="w-4" />
            <span>Pillar</span>
            <span>What every post in it does</span>
          </div>
          <ul className="space-y-4">
            {chosen.map((pillar, index) => {
              const problem = problems[index];
              const example = pillarExample(pillar.name, index);
              return (
                <li key={index} className={`grid gap-2 sm:items-start ${PILLAR_COLUMNS}`}>
                  <input
                    type="checkbox"
                    aria-label={`Use ${pillar.name}`}
                    checked={pillar.on}
                    className="mt-2.5 size-4"
                    onChange={(event) => update(index, { on: event.target.checked })}
                  />
                  <Input
                    aria-label="Pillar name"
                    aria-invalid={tried && problem === "Give it a name."}
                    maxLength={60}
                    value={pillar.name}
                    onChange={(event) => update(index, { name: event.target.value })}
                  />
                  <div className="space-y-1.5">
                    <Textarea
                      aria-label={`What every post in ${pillar.name || "this pillar"} does`}
                      aria-invalid={tried && problem === "Needs a sentence."}
                      aria-describedby={problem ? `pillar-hint-${index}` : undefined}
                      rows={2}
                      maxLength={600}
                      placeholder={`For example: ${example}`}
                      value={pillar.format}
                      onChange={(event) => update(index, { format: event.target.value })}
                    />
                    {problem === "Needs a sentence." && (
                      <p
                        id={`pillar-hint-${index}`}
                        className={`flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md px-2.5 py-1.5 text-xs ${
                          tried ? "bg-red-50 text-red-800" : "bg-paper text-muted"
                        }`}
                      >
                        <span>
                          {pillar.format.trim() ? "Too short: write a whole sentence. " : ""}For
                          example: <span className="italic text-ink">“{example}”</span>
                        </span>
                        <button
                          type="button"
                          className="font-medium text-accent underline underline-offset-2"
                          onClick={() => update(index, { format: example })}
                        >
                          Use this
                        </button>
                      </p>
                    )}
                    {problem === "Give it a name." && (
                      <p id={`pillar-hint-${index}`} className="text-xs text-red-700">
                        Give this pillar a name, or untick it.
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {tried && blocked && (
            <ErrorText>
              Write a sentence for the highlighted pillars (or use the examples), or untick the ones
              you don’t want.
            </ErrorText>
          )}
          {accept.isError && <ErrorText>{apiErrorMessage(accept.error)}</ErrorText>}
          {atLeast("EDITOR") && (
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={accept.isPending || !chosen.some((pillar) => pillar.on)}
                onClick={() => {
                  setTried(true);
                  if (blocked) return;
                  accept.mutate({
                    pillars: chosen
                      .filter((pillar) => pillar.on)
                      .map(({ name, format }) => ({ name, format })),
                  });
                }}
              >
                Add the ticked pillars
              </Button>
              {needSentence.length > 1 && (
                <Button variant="secondary" onClick={useExamples}>
                  Use the examples for the {needSentence.length} empty ones
                </Button>
              )}
              {setup.existingPillars > 0 && (
                <Button
                  variant="secondary"
                  disabled={accept.isPending}
                  onClick={() => accept.mutate({ pillars: [] })}
                >
                  Keep my pillars as they are
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </article>
  );
};

const RunLog = ({ setup }: { setup: SetupDto }) => {
  if (!setup.run) return null;
  const { run } = setup;
  return (
    <div className="space-y-3 rounded-xl border border-line bg-white p-4 text-sm">
      <h2 className="text-xs font-semibold uppercase tracking-widest text-muted">
        What the researcher did
      </h2>
      <ul className="space-y-1">
        <li>
          Read {run.pages.length} page{run.pages.length === 1 ? "" : "s"}
        </li>
        <li>
          Searched the web {run.searches} time{run.searches === 1 ? "" : "s"}
        </li>
        {run.refused.length > 0 && (
          <li className="text-muted">Couldn't open {run.refused.length}</li>
        )}
      </ul>
      <p className="text-xs text-muted">
        {run.endedAt ? `Finished ${formatRelative(run.endedAt)}` : "Working…"} · US$
        {run.costUsd.toFixed(2)}
      </p>
      {run.pages.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-accent">Every page it read</summary>
          <ul className="mt-2 space-y-1">
            {run.pages.map((page) => (
              <li key={page.url} className="truncate">
                <a
                  href={page.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent underline"
                >
                  {page.title || page.url}
                </a>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
